/**
 * 문항 카드 ⚡ 드롭다운 — 순수 UI. @zep-us/zus Dropdown, 슬라이더의 QuizItemToolsDropdown 과 같은 조립.
 * 비활성 항목은 흐리게 + 사유 툴팁(PRD §7-2). 단 `hideWhenIneligible` 항목(선지 채움)은 자격 미달이면
 * 아예 오지 않는다 — 목록 구성은 useQuestionCardAiMenuItems 몫. 데스크톱 전용(모바일은 EditToolsDrawer 항목).
 */
import { Separator } from '@zep-us/zus';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { BoltFillIcon, SparkleBulletPointsIcon } from '@/icons';
import { WithPlanBadge } from '@/shared/components/Badge/PlanBadge';
import { Dropdown, IconButton, Tooltip } from '@/ui_v3';
import { QUESTION_CARD_AI_PLAN_TYPE } from '../consts/questionCardAi.const';
import { QUESTION_CARD_AI_MENU_GROUP_ORDER } from '../consts/questionCardAiMenu.const';
import { useQuestionCardAiCopy } from '../hooks/useQuestionCardAiCopy';
import { type QuestionCardAiMenuItem } from '../types/questionCardAi.types';
export type QuestionCardAiMenuUIProps = {
  items: QuestionCardAiMenuItem[];
  /** 다른 AI 작업이 도는 중 — 트리거 아이콘을 흐리게 그린다. */
  isLocked: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};
const MenuItemWithReason = ({
  item
}: {
  item: QuestionCardAiMenuItem;
}) => {
  // ZUS DropdownItem 은 disabled 를 스타일에만 쓰고 Radix 로 넘기지 않는다
  // (pointer-events-none 뿐이라 키보드 선택은 그대로 통과한다). 실행 차단은 여기서 한다.
  const menuItem = <Dropdown.Item disabled={item.disabled} aria-disabled={item.disabled || undefined} onSelect={event => {
    if (item.disabled) {
      event.preventDefault();
      return;
    }
    item.onSelect();
  }} className="h-10 gap-2xs">
      {item.icon}
      {item.label}
    </Dropdown.Item>;
  if (!item.disabled || !item.reason) return menuItem;
  return <Tooltip.Provider data-sentry-element="unknown" data-sentry-component="MenuItemWithReason" data-sentry-source-file="QuestionCardAiMenuUI.tsx">
      <Tooltip.Root data-sentry-element="unknown" data-sentry-source-file="QuestionCardAiMenuUI.tsx">
        <Tooltip.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="QuestionCardAiMenuUI.tsx">
          <span className="block">{menuItem}</span>
        </Tooltip.Trigger>
        <Tooltip.Content data-sentry-element="unknown" data-sentry-source-file="QuestionCardAiMenuUI.tsx">{item.reason}</Tooltip.Content>
      </Tooltip.Root>
    </Tooltip.Provider>;
};
export const QuestionCardAiMenuUI = ({
  items,
  isLocked,
  open,
  onOpenChange
}: QuestionCardAiMenuUIProps) => {
  const copy = useQuestionCardAiCopy();
  return <>
      <Dropdown.Root modal={false} open={open} onOpenChange={onOpenChange} data-sentry-element="unknown" data-sentry-source-file="QuestionCardAiMenuUI.tsx">
        <WithPlanBadge isBadgeVisible planType={QUESTION_CARD_AI_PLAN_TYPE} wrapperClassName="hidden mobile-lg:block"
      // Figma: 트리거 기준 x+20 / y-5, 높이 14 + 안쪽 2 + 바깥 테두리 1. 클릭은 트리거로 통과시킨다.
      className="pointer-events-none right-[-5px] top-[-6px] size-4 translate-x-0 translate-y-0 border p-[2px] shadow-md" glyph={<BoltFillIcon width={10} height={10} fill={ICON_COLOR.inverse} aria-hidden />} data-sentry-element="WithPlanBadge" data-sentry-source-file="QuestionCardAiMenuUI.tsx">
          <Dropdown.Trigger asChild data-sentry-element="unknown" data-sentry-source-file="QuestionCardAiMenuUI.tsx">
            <IconButton size="xs" variant="outlined" intent="neutral" aria-label={copy.trigger} data-state={open ? 'open' : 'closed'} className="data-[state=open]:bg-opacity-black-1" onMouseDown={event => {
            event.preventDefault();
            event.stopPropagation();
          }} onClick={event => event.stopPropagation()} data-sentry-element="IconButton" data-sentry-source-file="QuestionCardAiMenuUI.tsx">
              <SparkleBulletPointsIcon size="xs" fill={isLocked ? ICON_COLOR.disabled : ICON_COLOR.default} data-sentry-element="SparkleBulletPointsIcon" data-sentry-source-file="QuestionCardAiMenuUI.tsx" />
            </IconButton>
          </Dropdown.Trigger>
        </WithPlanBadge>
        <Dropdown.Portal data-sentry-element="unknown" data-sentry-source-file="QuestionCardAiMenuUI.tsx">
          <Dropdown.Content hideWhenDetached side="bottom" align="end" className="w-[240px] flex-col border-assistive px-xs py-3xs shadow-lg" onCloseAutoFocus={event => event.preventDefault()} data-sentry-element="unknown" data-sentry-source-file="QuestionCardAiMenuUI.tsx">
            {QUESTION_CARD_AI_MENU_GROUP_ORDER.map(group => {
            const groupItems = items.filter(item => item.group === group);
            if (groupItems.length === 0) return null;
            return <div key={group}>
                  {groupItems.map(item => <MenuItemWithReason key={item.key} item={item} />)}
                </div>;
          })}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      {/* Figma: AI 트리거와 복제/삭제 사이 22px 구분선. 트리거와 같이 모바일에선 숨긴다. */}
      <Separator orientation="vertical" className="hidden h-[22px] self-center bg-gray-200 mobile-lg:block" data-sentry-element="Separator" data-sentry-source-file="QuestionCardAiMenuUI.tsx" />
    </>;
};